import { useSidebarNavStore } from '@/platform/stores/useSidebarNavStore';
import {
    Badge,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import {
    ArrowTopRightOnSquareIcon,
    Bars3Icon,
    BugAntIcon,
    ClockIcon,
    CloudArrowUpIcon,
    DocumentCheckIcon,
    DocumentDuplicateIcon,
    MagnifyingGlassIcon,
    PhoneIcon,
    PrinterIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage, usePoll } from '@inertiajs/react';
import { useEffect, useMemo } from 'react';
import CustomerSearch from '../CustomerSearch/CustomerSearch';
import NavigationItem from './components/NavigationItem';
import NavigationMenu from './components/NavigationMenu';
import NavigationSeparator from './components/NavigationSeparator';

const TopNavigation = () => {
    const {
        auth,
        wormhole_counter,
        actions_counter,
        dgr_counter,
        phonecalls_counter,
        print_jobs_counter,
    } = usePage().props;
    const { setMobileOpen } = useSidebarNavStore();

    // TODO: Double check if we still need window.laravelEcho.private('global-counters')...
    usePoll(5 * 60 * 1000, {
        only: [
            'wormhole_counter',
            'actions_counter',
            'dgr_counter',
            'phonecalls_counter',
            'print_jobs_counter',
        ],
    });

    const wormholeColor = useMemo(() => {
        if (wormhole_counter?.Failed > 0) {
            return 'red';
        }
        if (wormhole_counter?.['Action Required'] > 0) {
            return 'yellow';
        }
        if (wormhole_counter?.Processing > 0) {
            return 'blue';
        }

        return 'grey';
    }, [wormhole_counter]);

    const wormholeCount = useMemo(
        () =>
            Object.keys(wormhole_counter || {}).reduce(
                (total, key) => total + wormhole_counter[key],
                0
            ),
        [wormhole_counter]
    );

    const openQuickjump = () => {
        router.visit('/quickjump', {
            preserveState: true,
            preserveScroll: true,
            replace: false,
        });
    };

    useEffect(() => {
        const handleKeyDown = event => {
            if (
                (event.metaKey || event.ctrlKey) &&
                (event.key === 'k' || event.key === '/')
            ) {
                event.preventDefault();

                openQuickjump();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <NavigationMenu>
            <NavigationItem
                as='button'
                type='button'
                onClick={() => setMobileOpen(true)}
                className='lg:hidden'
            >
                <span className='sr-only'>Open sidebar</span>
                <Bars3Icon aria-hidden='true' className='size-4' />
            </NavigationItem>

            <Menu>
                <MenuButton as={NavigationItem}>
                    <span className='sr-only'>Open user menu</span>
                    <UserIcon aria-hidden='true' className='size-4' />
                    <Badge color={actions_counter > 0 ? 'red' : 'grey'}>
                        {actions_counter || 0}
                    </Badge>
                </MenuButton>
                <MenuItems>
                    {auth?.permissions?.includes('view actions') && (
                        <MenuItem as='a' href='/actions'>
                            Acties
                            {actions_counter > 0 && (
                                <Badge color='red'>{actions_counter}</Badge>
                            )}
                        </MenuItem>
                    )}
                    {auth?.permissions?.includes('create actions') && (
                        <MenuItem
                            onClick={() =>
                                router.visit('/actions/create', {
                                    preserveState: true,
                                    preserveScroll: true,
                                    replace: false,
                                })
                            }
                        >
                            Nieuwe actie
                        </MenuItem>
                    )}
                    {auth?.permissions?.includes('create notes') && (
                        <MenuItem
                            onClick={() =>
                                router.visit('/notes/create', {
                                    preserveState: true,
                                    preserveScroll: true,
                                    replace: false,
                                })
                            }
                        >
                            Nieuwe notitie
                        </MenuItem>
                    )}
                    {['view tickets', 'view all tickets']?.some(permission =>
                        auth?.permissions?.includes(permission)
                    ) && (
                        <>
                            <MenuSeparator />
                            <MenuItem as='a' href='/tickets'>
                                Tickets
                            </MenuItem>
                            <MenuItem
                                as='a'
                                target='_blank'
                                href='https://vcsw.atlassian.net/servicedesk/customer/portal/3/group/11/create/334'
                                rel='noopener noreferrer'
                            >
                                Jira ticket
                                <ArrowTopRightOnSquareIcon className='size-4' />
                            </MenuItem>
                        </>
                    )}
                    <MenuSeparator />
                    <MenuItem href='/account' as={Link}>
                        Mijn account
                    </MenuItem>
                    {auth?.is_impersonated && (
                        <MenuItem
                            as={Link}
                            method='post'
                            href='/impersonate/logout'
                        >
                            Terug naar eigen account
                        </MenuItem>
                    )}
                    <MenuItem as={Link} method='post' href='/logout'>
                        Uitloggen
                    </MenuItem>
                </MenuItems>
            </Menu>

            <NavigationItem>
                <CustomerSearch />
            </NavigationItem>

            {['view calendar', 'view all customers resources'].some(
                permission => auth?.permissions?.includes(permission)
            ) && <NavigationSeparator />}

            {auth?.permissions?.includes('view calendar') && (
                <NavigationItem as='a' href='/calendar'>
                    <PhoneIcon aria-hidden='true' className='size-4' />
                    <Badge color='yellow'>{phonecalls_counter || 0}</Badge>
                </NavigationItem>
            )}
            {['view all customers resources', 'view assessments'].every(
                permission => auth?.permissions?.includes(permission)
            ) && (
                // @IMPORTANT: This page does not exists
                <NavigationItem as='a' href='/assessments?no-status=1&fullDb=1'>
                    <DocumentDuplicateIcon
                        aria-hidden='true'
                        className='size-4'
                    />
                    <Badge color='red'>0</Badge>
                </NavigationItem>
            )}

            {[
                'view improved wormhole',
                'view queue',
                'view print list',
                'start dgr bot',
                'view dgv',
            ]?.some(permission => auth?.permissions?.includes(permission)) && (
                <NavigationSeparator />
            )}

            {auth?.permissions?.includes('view improved wormhole') && (
                <Menu>
                    <MenuButton as={NavigationItem}>
                        <CloudArrowUpIcon
                            aria-hidden='true'
                            className='size-4'
                        />
                        <Badge color={wormholeColor}>{wormholeCount}</Badge>
                    </MenuButton>
                    <MenuItems modal={true}>
                        <MenuItem
                            as='a'
                            href='/wormhole'
                            badge={{ content: wormhole_counter?.Queued || 0 }}
                        >
                            In de wachtrij
                        </MenuItem>
                        <MenuItem
                            as='a'
                            href='/wormhole?tab=in-progress'
                            badge={{
                                color: 'blue',
                                content: wormhole_counter?.Processing || 0,
                            }}
                        >
                            In behandeling
                        </MenuItem>
                        <MenuItem
                            as='a'
                            href='/wormhole?tab=action-required'
                            badge={{
                                color: 'yellow',
                                content:
                                    wormhole_counter?.['Action Required'] || 0,
                            }}
                        >
                            Actie vereist
                        </MenuItem>
                        <MenuItem
                            as='a'
                            href='/wormhole?tab=failed'
                            badge={{
                                color: 'red',
                                content: wormhole_counter?.['Failed'] || 0,
                            }}
                        >
                            Gefaald
                        </MenuItem>
                    </MenuItems>
                </Menu>
            )}

            {['view queue', 'view old ingest tools'].every(permission =>
                auth?.permissions?.includes(permission)
            ) && (
                // @IMPORTANT: This page is outdated and possibly not used
                <NavigationItem as='a' href='/queue'>
                    <ClockIcon aria-hidden='true' className='size-4' />
                    <Badge color='blue'>0</Badge>
                </NavigationItem>
            )}

            {auth?.permissions?.includes('view print list') && (
                <NavigationItem as='a' href='/print-jobs'>
                    <PrinterIcon aria-hidden='true' className='size-4' />
                    <Badge color='blue'>{print_jobs_counter || 0}</Badge>
                </NavigationItem>
            )}

            {auth?.permissions?.includes('start dgr bot') && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavigationItem>
                            <BugAntIcon aria-hidden='true' className='size-4' />
                            <Badge color='blue'>{`${dgr_counter?.employees || 0}/${dgr_counter?.entrants || 0}`}</Badge>
                        </NavigationItem>
                    </TooltipTrigger>
                    <TooltipContent>Werknemers/Instroomers</TooltipContent>
                </Tooltip>
            )}

            {auth?.permissions?.includes('view dgv') && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavigationItem
                            as={Link}
                            href={`/customers/${auth?.user?.selected_customer_id}/services/lkv/dgv-list`}
                        >
                            <DocumentCheckIcon
                                aria-hidden='true'
                                className='size-4'
                            />
                        </NavigationItem>
                    </TooltipTrigger>
                    <TooltipContent>Doelgroepverklaringen</TooltipContent>
                </Tooltip>
            )}

            <NavigationSeparator />

            <NavigationItem onClick={openQuickjump}>
                <span className='flex items-center gap-x-2 rounded-full border border-gray-300 py-1 pr-2 pl-3 text-xs text-gray-500 transition-colors duration-200 hover:border-gray-400 hover:text-gray-700'>
                    <MagnifyingGlassIcon
                        aria-hidden='true'
                        className='size-3'
                    />
                    <kbd className='font-sans text-xs'>Ctrl /</kbd>
                </span>
            </NavigationItem>
        </NavigationMenu>
    );
};

export default TopNavigation;
